/**
 * Overlay Button Component
 * A reusable button component for overlay contexts (e.g., image grids)
 * Features semi-transparent background with solid border
 */

import React, { useState, useEffect, useMemo } from 'react';
import { ButtonHTMLAttributes } from 'react';
import { fontPrimary } from '@/design-system/utils';

export interface OverlayButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Button label text
   */
  label: string;
  /**
   * Click handler
   */
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /**
   * Optional custom styles to override defaults
   */
  style?: React.CSSProperties;
  /**
   * Optional className
   */
  className?: string;
}

export const OverlayButton: React.FC<OverlayButtonProps> = ({
  label,
  onClick,
  style,
  className = '',
  ...props
}) => {
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );

  // Add resize listener
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Responsive positioning and sizing (base inlined for exhaustive-deps)
  const responsiveStyles = useMemo(() => {
    const isTablet = windowWidth <= 768;
    const isMobile = windowWidth <= 480;
    const baseStyles: React.CSSProperties = {
      display: 'inline-flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '8px',
      position: 'absolute',
      borderRadius: '8px',
      border: '0.5px solid #393737',
      background: 'rgba(0, 0, 0, 0.30)',
      boxShadow: '0 4px 4px rgba(0, 0, 0, 0.25)',
      fontFamily: fontPrimary,
      fontStyle: 'normal',
      fontWeight: 600,
      lineHeight: 'normal',
      color: '#FFF',
      cursor: 'pointer',
      zIndex: 10,
      whiteSpace: 'nowrap',
    };
    return {
      ...baseStyles,
      // Desktop: 13px right, 16px bottom, 16px font
      // Tablet: 8px right, 12px bottom, 14px font
      // Mobile: 6px right, 10px bottom, 13px font
      right: isMobile ? '6px' : isTablet ? '8px' : '13px',
      bottom: isMobile ? '10px' : isTablet ? '12px' : '16px',
      padding: isMobile ? '6px 8px' : isTablet ? '6px 10px' : '8px 12px',
      fontSize: isMobile ? '13px' : isTablet ? '14px' : '16px',
    };
  }, [windowWidth]);

  return (
    <button
      type='button'
      onClick={onClick}
      style={{ ...responsiveStyles, ...style }}
      className={className}
      {...props}
    >
      {label}
    </button>
  );
};

export default OverlayButton;
